import { giphyDarkestGrey, giphyLightestGrey } from '@giphy/colors'
import { uniqueId } from 'lodash'
import { useContext, useMemo } from 'react'
import { Range } from 'types'
import IconComponent from 'ui/src/components/icon'
import { UGEContext } from '../../../context'
import { UGELayer, UGELayerAction, ugeLayerActions } from '../../../types'
import { gaValueFromLayerAction, iconFromLayerAction, labelFromLayerAction } from '../../../utilities/layer-action'
import { Button, Container, TimeInput } from './style'

type Props = {
    className?: string
    editor?: string
    layer: UGELayer
}

const UGEEditorLayerActions = ({ className, editor, layer }: Props) => {
    const { addLayer, duration, layerActionLabels, layers, removeLayerById, updateLayerById, setLayers, trackGAEvent } =
        useContext(UGEContext)
    const timing: Range = useMemo(() => layer.props.timing || { start: 0, end: 1 }, [layer.props])
    const startTime = duration * timing.start
    const endTime = duration * timing.end

    function onActionClick(action: UGELayerAction) {
        switch (action) {
            case 'delete':
                removeLayerById(layer.id)
                break
            case 'duplicate':
                addLayer({ ...layer, id: uniqueId(`uge-duplicate-`) })
                break
            case 'mirror':
                updateLayerById(layer.id, {
                    ...layer.props,
                    mirrored: layer.props.mirrored ? false : true,
                })
                break
            case 'send-to-back':
                setLayers([layer, ...layers.filter((l) => l.id !== layer.id)])
                break
        }

        trackGAEvent('creation_asset_menu_option', {
            content: {
                contentId: layer.gifId,
                contentType: layer.type === 'image' ? undefined : layer.type,
            },
            creation: {
                assetMenuObject: editor,
                contentEditAction: gaValueFromLayerAction(action),
            },
        })
    }

    function onStartTimeChange(value: number) {
        updateLayerById(layer.id, {
            timing: {
                ...timing,
                start: Math.round((value / duration) * 100) / 100,
            },
        })
    }

    function onEndTimeChange(value: number) {
        updateLayerById(layer.id, {
            timing: {
                ...timing,
                end: Math.round((value / duration) * 100) / 100,
            },
        })
    }

    return (
        <Container className={className}>
            {duration > 0 && (
                <TimeInput
                    format="time"
                    max={endTime}
                    min={0}
                    size="medium"
                    step={0.05}
                    value={startTime}
                    onChange={onStartTimeChange}
                />
            )}
            {ugeLayerActions.map((action) => {
                const icon = iconFromLayerAction(action)
                const label = labelFromLayerAction(action, layerActionLabels)

                return (
                    <Button
                        color={giphyDarkestGrey}
                        icon={<IconComponent color={giphyLightestGrey} height={16} icon={icon} width={18} />}
                        iconLocation="left"
                        key={`uge-layer-action-${action}`}
                        size="small"
                        textColor={giphyLightestGrey}
                        onClick={() => onActionClick(action)}
                    >
                        {label}
                    </Button>
                )
            })}
            {duration > 0 && (
                <TimeInput
                    format="time"
                    max={duration}
                    min={startTime}
                    size="medium"
                    step={0.05}
                    value={endTime}
                    onChange={onEndTimeChange}
                />
            )}
        </Container>
    )
}

export default UGEEditorLayerActions
